import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { LoaderService } from '../../../shared/services';
import {
  ProductInterface,
  ProductOptionsInterface,
  ProductPayloadInterface,
  ProductQueryParamsInterface,
  ProductSearchResponseInterface
} from '../interfaces';
import { ResponseApiInterface } from '../../../core/interfaces';

type ProductHttpParamValueType =
  | string
  | number
  | boolean
  | readonly (string | number | boolean)[];

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly baseURL: string = `${environment.url}/${environment.prefix}`;
  private readonly url: string = `${this.baseURL}/products`;
  constructor(private readonly http: HttpClient, private readonly loader: LoaderService) {}

  getProducts(
    params: ProductQueryParamsInterface = {}
  ): Observable<ResponseApiInterface<ProductInterface[]>> {
    return this.http.get<ResponseApiInterface<ProductInterface[]>>(this.url, {
      params: this.toHttpParams(params)
    });
  }

  getProductDetails(
    productId: string,
    params: ProductQueryParamsInterface = {}
  ): Observable<ResponseApiInterface<ProductInterface>> {
    const url: string = `${this.url}/${productId}/details`;
    return this.loader.runWithLoader(
      this.http.get<ResponseApiInterface<ProductInterface>>(url, {
        params: this.toHttpParams(params)
      }),
      [
        'Buscando os dados do produto...',
        'Coletando informações atualizadas...',
        'Verificando os registros disponíveis...',
        'Só um instante, estamos acessando os detalhes...',
        'Ainda carregando... Estamos quase lá.',
        'Aguarde mais um pouco! Garantindo que tudo seja exibido corretamente...'
      ]
    );
  }

  getProductById(productId: string): Observable<ResponseApiInterface<ProductInterface>> {
    const url: string = `${this.url}/${productId}`;
    return this.loader.runWithLoader(this.http.get<ResponseApiInterface<ProductInterface>>(url), [
      'Carregando informações do produto...',
      'Buscando detalhes técnicos...',
      'Verificando disponibilidade em estoque...',
      'Aguarde um momento...'
    ]);
  }

  createProduct(
    data: Partial<ProductPayloadInterface>
  ): Observable<ResponseApiInterface<ProductInterface>> {
    return this.loader.runWithLoader(
      this.http.post<ResponseApiInterface<ProductInterface>>(this.url, data),
      [
        'Iniciando o cadastro do produto...',
        'Validando os dados informados...',
        'Verificando se está tudo certo com o cadastro...',
        'Aguarde só um instante, estamos quase lá...',
        'Ainda processando... Isso está demorando um pouco mais que o normal.',
        'Desculpe a demora! Garantindo que tudo seja concluído com sucesso...'
      ]
    );
  }

  updateProduct(
    productId: string,
    data: Partial<ProductPayloadInterface>
  ): Observable<ResponseApiInterface<ProductInterface>> {
    const url: string = `${this.url}/${productId}`;
    return this.loader.runWithLoader(
      this.http.patch<ResponseApiInterface<ProductInterface>>(url, data),
      [
        'Iniciando o cadastro do produto...',
        'Validando os dados informados...',
        'Verificando se está tudo certo com o cadastro...',
        'Aguarde só um instante, estamos quase lá...',
        'Ainda processando... Isso está demorando um pouco mais que o normal.',
        'Desculpe a demora! Garantindo que tudo seja concluído com sucesso...'
      ]
    );
  }

  deleteProduct(productId: string): Observable<void> {
    const url: string = `${this.url}/${productId}`;
    return this.loader.runWithLoader(this.http.delete<void>(url), ['Excluindo...']);
  }

  searchProducts(
    query: string
  ): Observable<ResponseApiInterface<ProductSearchResponseInterface[]>> {
    const url: string = `${this.url}/search`;
    return this.http.get<ResponseApiInterface<ProductSearchResponseInterface[]>>(url, {
      params: { q: query, available: 'true' }
    });
  }

  private toHttpParams(
    params: ProductQueryParamsInterface
  ): Record<string, ProductHttpParamValueType> {
    const httpParams: Record<string, ProductHttpParamValueType> = {};

    Object.keys(params).forEach((key) => {
      const value = params[key];

      if (value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      httpParams[key] = value;
    });

    return httpParams;
  }
}
